import React, {useEffect, useRef, useState} from 'react';
import Popup from "reactjs-popup";
import {useSelector} from "react-redux";

// Icon & Framework
import * as Icon from "ubs-icons";
import UAvatar from "ubs-framework/v2/UAvatar";
import UPopup from "ubs-framework/v2/UPopup";
import {COLOR, PROFILE_SECTION} from "ubs-framework/v2/common/constant";
import {useFieldLabel} from "ubs-framework/v2/hooks/useCustomFieldConfig";
import {getKeyValue} from "ubs-framework/v2/common/function";
import {DoubleEncryption, getName, isMobileDevice, isMobileTablet, getThumbor} from "ubs-framework/v2/common/function";

import {sendData} from "../../../store/actions";
import useSocket from "../hooks/useSocket";

import Skeleton from "ubs-framework/v2/skeleton";
const Avatar = React.memo(({user, width}) => {

    const getLabel = useFieldLabel();
    const popupRef = useRef(null)
    const _ORG_ID = useSelector(e => e.common.orgId);
    const _S3_BASE_URL = useSelector(e => e.common.S3_BASE_URL);
    const _JTL_KV = useSelector(e => getKeyValue(e.hrms?.jobTitleList));
    const _DL_KV = useSelector(e => getKeyValue(e.hrms?.departmentList));
    const _LL_KV = useSelector(e => getKeyValue(e.hrms?.branchList));
    const _PLAN = useSelector(e => e?.common?.planAccess);
    const _SDL_KV = useSelector(e => getKeyValue(e.hrms?.subDepartmentList));
    const _ID = useSelector(e => e.common._id);

    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(true)
    const [empData, setEmpData] = useState({})

    useEffect(() => {
        return () => off();
    }, []);

    const gotoChat = (emp) => {
        if ((isMobileDevice() || isMobileTablet()) && _PLAN?.chat?.default?.access)  window.open(`${process.env.REACT_APP_CHAT_MODULE_URL}/${_ORG_ID}/chat#pm/${emp?.user_id}`, '_blank');
        else {
            const data = {
                code: 802,
                data: {contactId: emp._id}
            }
            window.postMessage({bridge: 'ubs-bridge', payload: DoubleEncryption(data)})
        }
    }

    const {on, off, refresh} = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'hrms|employee|userInfo|read':
                    setLoading(false)
                    setEmpData(res?.data)
                    break
                default:
                    return true;
            }
        }
    });

    // events
    const handle = {
        open: e => {
            const data = {
                user: user?.user || user?._id || user?.value,
            }
            sendData('hrms|employee|userInfo|read', data);
            refresh();
            setOpen(true)
        },
        close: e => {
            if (popupRef.current) popupRef.current.close();
        },
        goToProfile: user => {
            window.open(`${process.env.REACT_APP_HRMS_MODULE_URL}/${_ORG_ID}/${user?._id === _ID ? 'my-profile' : `employees/${user?._id}`}/personal/info`, '_blank');
        }
    }

    return (
        <React.Fragment>
            <Popup
                ref={popupRef}
                trigger={open => (
                    <div className="u_cp u_flex-shrink">
                        <UAvatar name={getName(user)} image={user?.profileImage}
                                 width={width ? width : 36} s3Url={_S3_BASE_URL}
                                 thumbor={getThumbor()}
                        />
                    </div>
                )}
                open={false}
                on={user?.user || user?._id || user?.value ? ['hover', 'focus'] : []}
                // on={['click']}
                position="right center"
                closeOnDocumentClick
                repositionOnResize
                keepTooltipInside={true}
                className=""
                onOpen={() => handle.open()}
                onClose={() => handle.close()}
                mouseEnterDelay={1000}
                contentStyle={{zIndex: 11111}}
                disabled={!user?.user && !user?._id && !user?.value}
            >
                <div className="u_employee-popup">
                    <div className="u_employee-popup-header">
                        <div className="u_employee-header-left">
                            <div className="u_employee-image u_flex-shrink">
                                <UAvatar name={getName(empData)} image={empData?.profileImage}
                                         width={36} s3Url={_S3_BASE_URL}
                                         thumbor={getThumbor()}/>
                            </div>
                            <div className="u_employee-name">
                                {!loading ?
                                    <h4 className="u_one-line">{getName(empData)}</h4> :
                                    <Skeleton className="u_mb-5" width="130px" height="14px"/>}
                                {!loading ? <span>{empData?.employeeId}</span> :
                                    <Skeleton className="u_mb-5" width="50px" height="20px"/>}
                            </div>
                        </div>
                        <div className="u_employee-header-right">
                            {_PLAN?.hrms?.default?.access && empData?.access && <span className="u_employee-action u_green" onClick={() => handle.goToProfile(empData)}>
                                <Icon.ArrowLeftLine size={14} fill={COLOR.u_green}/>
                            </span>}

                            <span className="u_employee-action u_blue" onClick={() => handle.close()}>
                                <Icon.Close size={14} fill={COLOR.u_blue}/>
                            </span>
                        </div>
                    </div>
                    <div className="u_employee-popup-body">
                        <ul className={`u_employee-details`}>
                            <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.BriefCase className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <span className="u_one-line u_text-c">{_DL_KV[empData?.department] || '-'}</span>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>
                            {empData?.subDepartment && <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.BriefCase className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <div className="u_table-long-text">
                                        {empData?.subDepartment ?
                                            <UPopup
                                                params={{className: "u_tooltip"}}
                                                desc={
                                                    <span>{getLabel(PROFILE_SECTION.jobInfo, 'subDepartment', 'Sub Department')}: <br/>{_SDL_KV[empData?.subDepartment] || '-'}</span>
                                                }
                                            >
                                                <span className="u_one-line u_text-c">{_SDL_KV[empData?.subDepartment] || '-'}</span>
                                            </UPopup>
                                            : <span className="u_event-none u_one-line">-</span>
                                        }
                                    </div>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>}
                            <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.Enterprise className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <span className="u_one-line u_text-c">{_JTL_KV[empData?.jobTitle] || '-'}</span>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>


                            <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.Mail className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <div className="u_table-long-text">
                                        {empData?.email ?
                                            <UPopup
                                                params={{className: "u_tooltip"}}
                                                desc={<span>{empData?.email}</span>}
                                            >
                                                <a href={`mailto:${empData?.email}`} className="u_one-line" target="_blank">
                                                    {empData?.email}
                                                </a>
                                            </UPopup>
                                            : <span className="u_event-none u_one-line">-</span>
                                        }
                                    </div>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>
                            <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.UserLocation className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <span className="u_one-line u_text-c">{_LL_KV[empData?.location] || '-'}</span>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>


                            <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.Contact className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <a href={`tel:${empData?.phoneNumber}`} className="u_one-line"
                                       target="_blank">{empData?.phoneNumber || '-'}</a>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>
                            <li>
                                {!loading ? <React.Fragment>
                                    <div className="u_flex-shrink">
                                        <Icon.EmergencyContact className="u_mr-10" fill={COLOR?.u_icon_bg} size={14}/>
                                    </div>
                                    <a href={`tel:${empData?.emergencyContact || '-'}`} target="_blank"
                                       className="u_one-line">{empData?.emergencyContact || '-'}</a>
                                </React.Fragment> : <Skeleton width="130px" height="14px"/>}
                            </li>
                        </ul>
                    </div>
                </div>
            </Popup>
        </React.Fragment>
    );
});

export default Avatar;
